import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { ChipModule } from 'primeng/chip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { InputIconModule } from 'primeng/inputicon';
import { FloatLabelModule } from 'primeng/floatlabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { CheckboxModule } from 'primeng/checkbox'
import { InputSwitchModule } from 'primeng/inputswitch';
import { ButtonModule } from 'primeng/button';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { BaseReport } from '../base-class/base-report.class';
import moment from 'moment';
import { ReportService } from '../reports.service';
import { StorageService } from '../../../../../shared/services/storage.service';
import { DateTimeService } from '../../../../../shared/services/date-time.service';
import { TimekeepingReportPayload, TimekeepingReportsResponse } from '../interfaces/reports.interface';
import { UserService } from '../../../../../shared/services/user.service';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import pdfMake from 'pdfmake/build/pdfmake';
import { constants } from '../../../../../shared/constants/constants';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-timekeeping-summary',
  standalone: true,
  imports: [CalendarModule,CheckboxModule,InputSwitchModule,OverlayPanelModule,FloatLabelModule,MultiSelectModule,DropdownModule, ButtonModule, FormsModule, ChipModule, IconFieldModule,InputIconModule,InputTextModule,TableModule, CommonModule, PipesModule ],
  templateUrl: './timekeeping-summary.component.html',
  styleUrl: './timekeeping-summary.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class TimekeepingSummaryComponent extends BaseReport implements OnInit {
  timekeepingReports: Array<TimekeepingReportsResponse> = [];
  showProjectSelectionError = false;
  useCustomDateRange = false; // New property for date toggle

  summary = {
    totalProjectHours: 0.00
  };

  constructor(
    override reportService: ReportService,
    override storageService: StorageService,
    override dateTimeService: DateTimeService,
    override userService: UserService
  ){
    super(reportService, storageService, dateTimeService, userService);
  }

  ngOnInit() {
    this.initializeReport();
    this.generateWeekDateAsPerFirm();
  }

  override async onProjectSelectionChange(e: any): Promise<void>{
    await super.onProjectSelectionChange(e);
    this.showProjectSelectionError = false;
  }

  slider(isNext: boolean): void{
    let md;
    if (isNext) {
      md = moment(this.startDate).add(7, 'day');
    } else {
      md = moment(this.startDate).subtract(7, 'day');
    }

    this.startDate = md.toDate();
    this.endDate = md.add(6, 'day').toDate();
  }

  getFormattedDateRange(): string{
    return `${moment(this.startDate).format('MM/DD/YYYY')} - ${moment(this.endDate).format('MM/DD/YYYY')}`
  }

  onDateToggleChange(event: any): void {
    // When toggling to custom date range, keep current dates
    // When toggling back to preset week, regenerate week dates
    if (!this.useCustomDateRange) {
      this.generateWeekDateAsPerFirm();
    }
  }

  onDateRangeChange(): void {
    // Called when custom date selection changes
    // You can add any validation or additional logic here
    if (this.startDate && this.endDate) {
      // Ensure end date is not before start date
      if (this.endDate < this.startDate) {
        this.endDate = new Date(this.startDate);
      }
      // Refresh reviewers when date range changes
      if (this.selectedProjects.length > 0) {
        this.getReviewersByProjectAndRoleIds();
      }
    }
  }

  override async getReviewersByProjectAndRoleIds(): Promise<void> {
    const projectIds = this.selectedProjects.map(project => project._id);
    const roleIds = this.selectedRoles.map(role => role.id);
    const firmId = this.sessionUser.firm.id;
    const startDate = moment(this.startDate).format('MM/DD/YYYY');
    const endDate = moment(this.endDate).format('MM/DD/YYYY');
    this.reviewers = await this.reportService.getSelectedProjectReviewers(projectIds, roleIds, firmId, startDate, endDate);
    this.reviewers.forEach(reviewer => {
        reviewer.fullName = `${reviewer.firstName} ${reviewer.lastName}`;
    });
    this.selectedReviewers = [];
  }

  generateReport(): void{
    if(!this.selectedProjects.length){
      this.showProjectSelectionError = true;
      return;
    }
    
    // Ensure we have valid dates
    if (!this.startDate || !this.endDate) {
      console.error('Start date or end date is missing');
      return;
    }
    
    const filters: TimekeepingReportPayload = {
      emails: "",
      firm: this.sessionUser.firm._id,
      fromDate: moment(this.startDate).format('MM/DD/YYYY'),
      showRates: true,
      groupByReviewer: false,
      includeZeroHours: false,
      invoiceWeekId: moment(this.startDate).add(2, 'days').isoWeek(),
      isFetchAllRecords: false,
      isSentEmail: false,
      project: this.selectedProjects.map(project => project._id),
      reviewer: this.selectedReviewers.map(reviewer => reviewer._id),
      role: this.selectedRoles.map(role => role.id),
      selectedProj: {...this.selectedProjects},
      timezone: this.isDefaultTimeZone ? constants.timezones[0] : this.selectedTimezone?.val || '',
      toDate: moment(this.endDate).format('MM/DD/YYYY'),
      user: this.sessionUser
    };

    this.reportService.generateTimekeepingReport(filters).subscribe({
      next: (timekeepingReports: Array<TimekeepingReportsResponse>) => {
        this.timekeepingReports = timekeepingReports;
        let totalProjectHours = 0;
        this.timekeepingReports.forEach(report => {
          totalProjectHours += Number(report['Hours worked'] || 0);
        });
        this.summary.totalProjectHours = parseFloat(totalProjectHours.toFixed(2));
      }
    });

  }



  loopOverToMake(groupedList: any, rows: any, roundParamTo: string) {
    groupedList.forEach((report: any) => {
        let cols = [
            this.generateColumn(report.Date, { fillColor: '#bdbdbd' }),
            this.generateColumn(report.Reviewer, { fillColor: '#bdbdbd' }),
            this.generateColumn(report.Project, { fillColor: '#bdbdbd' }),
            this.generateColumn(report.Role, { fillColor: '#bdbdbd' }),
            this.generateColumn(this.convertDecimalTo(roundParamTo, report['Hours worked'] || 0), { fillColor: '#bdbdbd' }),
            this.generateColumn(report.Rate || '-', { fillColor: '#bdbdbd' }),
            this.generateColumn(report.Total || '-', { fillColor: '#bdbdbd' }),
        ];

        rows.push(cols);
    });
    return rows;
  }

  exportPDF(roundParamTo: string) {
    let rows = [];
    const headerOptions = {
        bold: true,
        alignment: 'justify',
        width: '25%'
    };
    
    let headers = [
        this.generateColumn('Date', headerOptions),
        this.generateColumn('Reviewer', headerOptions),
        this.generateColumn('Project', headerOptions),
        this.generateColumn('Role', headerOptions),
        this.generateColumn('Hours Worked', headerOptions),
        this.generateColumn('Rate', headerOptions),
        this.generateColumn('Total', headerOptions),
    ];

    let widths = [65, 85, 95, 80, 75, 55, 55];

    rows.push(headers);
    
    rows = this.loopOverToMake(this.timekeepingReports, rows, roundParamTo);
    
    if (this.timekeepingReports && this.timekeepingReports.length) {
        const docDefinition = {
            pageOrientation: 'landscape',
            content: [
                {
                    text: this.sessionUser.firm.name,
                    style: 'header',
                    alignment: 'left',
                    fontSize: 22,
                    bold: true
                },
                {
                    text: ' ',
                    style: 'header'
                },
                {
                    text: ' ',
                    style: 'header'
                },
                {
                    text: 'Timekeeping Summary Report From: ' + moment(this.startDate).format('MM/DD/YYYY')  + ' To: ' + moment(this.endDate).format('MM/DD/YYYY') ,
                    style: 'header',
                    alignment: 'left',
                    fontSize: 16,
                    bold: true
                },
                {
                    text: ' ',
                    style: 'header'
                },
                {
                    text: ' ',
                    style: 'header'
                },
                {
                    text: 'Total Week Hours: ' + this.convertDecimalTo(roundParamTo, this.summary.totalProjectHours),
                    style: 'header',
                    alignment: 'right',
                    fontSize: 16,
                    bold: true
                },
                {
                    text: ' ',
                    style: 'header'
                },
                {
                    text: ' ',
                    style: 'header'
                },
                {
                    layout: 'lightHorizontalLines',
                    table: {
                        headerRows: 1,
                        body: rows,
                        widths: widths
                    }
                }
            ]
        } as TDocumentDefinitions;
        pdfMake.createPdf(docDefinition).download(new Date().getTime() + '-timekeeping-summary-report-from-' +
        moment(this.startDate).format('MM/DD/YYYY')   + '-to-' +  moment(this.endDate).format('MM/DD/YYYY')  );
    }
  }

  exportCSV(roundParamTo: string) {
    const fileData = this.createCSVHeader(roundParamTo);
    
    if (!this.timekeepingReports || !this.timekeepingReports.length) {
      return;
    }
    
    this.addReportDataToCSV(fileData, roundParamTo);
    this.downloadCSVFile(fileData);
  }

  private createCSVHeader(roundParamTo: string): string[] {
    return [
      this.sessionUser.firm.name + '\r\n\r\n',
      'Timekeeping Summary Report From: ' + moment(this.startDate).format('MM/DD/YYYY') + ' To: ' + moment(this.endDate).format('MM/DD/YYYY') + '\r\n\r\n',
      'Total Week Hours: ' + this.convertDecimalTo(roundParamTo, this.summary.totalProjectHours) + '\r\n\r\n',
      'Date,Reviewer,Project,Role,Hours Worked,Rate,Total \r\n'
    ];
  }

  private addReportDataToCSV(fileData: string[], roundParamTo: string): void {
    this.timekeepingReports.forEach(report => {
      this.addReportToCSV(fileData, report, roundParamTo);
    });
  }

  private addReportToCSV(fileData: string[], report: any, roundParamTo: string): void {
    const reportData = this.createReportCSVRow(report, roundParamTo);
    fileData.push(reportData);
  }

  private createReportCSVRow(report: any, roundParamTo: string): string {
    return [
      report.Date,
      report.Reviewer,
      report.Project,
      report.Role,
      report['Hours worked'] || '-',
      report.Rate || '-',
      report.Total || '-'
    ].join(',') + '\r\n';
  }

  private downloadCSVFile(fileData: string[]): void {
    const blob = new Blob(fileData, { type: "text/plain;charset=utf-8" });
    const fileName = this.generateCSVFileName();
    saveAs(blob, fileName);
  }

  private generateCSVFileName(): string {
    const timestamp = new Date().getTime();
    const fromDate = moment(this.startDate).format('MM/DD/YYYY');
    const toDate = moment(this.endDate).format('MM/DD/YYYY');
    return `${timestamp}-timekeeping-summary-report-from-${fromDate}-to-${toDate}.csv`;
  }

  
  override generateWeekDateAsPerFirm(): void {
    const day = 'Monday';
    let md = moment();
    while (md.format('dddd').toLowerCase() !== day.toLowerCase()) {
        md = md.subtract(1, 'day');
    }
    this.startDate = md.toDate();
    this.endDate = md.add(6, 'day').toDate();
  }
}